import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Grid, TextField, Button, LinearProgress, Box, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import { fetchAllCuestionarios } from '../api/cuestionariosApi';

const SORT_OPTIONS = [
  { value: 'fecha_desc', label: 'Más reciente', model: [{ field: 'fecha', sort: 'desc' }] },
  { value: 'pacienteId_asc', label: 'ID', model: [{ field: 'pacienteId', sort: 'asc' }] },
  { value: 'paciente_asc', label: 'Nombre', model: [{ field: 'paciente', sort: 'asc' }] },
];

const getTypeKey = (item) => String(item?.tipo ?? item?.tipoCuestionario ?? '').toLowerCase();
const getCategoryId = (item) => Number(item?.categoria ?? item?.tipoId ?? item?.cuestionarioId ?? item?.otrosId);
const isTesisTorres = (item) => {
  const type = getTypeKey(item);
  const category = getCategoryId(item);
  return type === 'tesis' || type === 'tesistorres' || type === 'tesis torres' || category === 3;
};

export default function CuestionarioTesisTorresTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPaciente, setFilterPaciente] = useState('');
  const [filterProfesional, setFilterProfesional] = useState('');
  const [sortModel, setSortModel] = useState([{ field: 'fecha', sort: 'desc' }]);
  const navigate = useNavigate();

  const currentSort = SORT_OPTIONS.find(
    o => o.model[0]?.field === sortModel[0]?.field && o.model[0]?.sort === sortModel[0]?.sort
  )?.value ?? 'fecha_desc';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllCuestionarios();
      const formatted = data
        .filter(isTesisTorres)
        .map((item, index) => {
          const pacienteId = item.paciente?.id ?? item.pacienteId ?? item.paciente_id;
          const d = new Date(item.createdAt);
          const fecha = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
          return {
            id: item.id ?? item.codigo ?? `tesis-${pacienteId ?? 'na'}-${index}`,
            pacienteId,
            paciente: item.paciente ? `${item.paciente.firstname} ${item.paciente.lastname}` : 'N/A',
            profesional: item.user ? `${item.user.firstname} ${item.user.lastname}` : 'N/A',
            fechaRaw: item.createdAt,
            fecha,
          };
        });
      setRows(formatted);
    } catch (err) {
      console.error('Error fetching cuestionarios Tesis Torres:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredRows = rows.filter(row =>
    (!filterPaciente || row.paciente.toLowerCase().includes(filterPaciente.toLowerCase())) &&
    (!filterProfesional || row.profesional.toLowerCase().includes(filterProfesional.toLowerCase()))
  );

  const columns = [
    { field: 'pacienteId', headerName: 'ID', width: 30 },
    { field: 'paciente', headerName: 'Paciente', width: 180 },
    { field: 'profesional', headerName: 'Profesional', width: 180 },
    {
      field: 'fecha', headerName: 'Fecha', width: 120,
      sortComparator: (v1, v2, cp1, cp2) =>
        new Date(cp1.api.getRow(cp1.id).fechaRaw) - new Date(cp2.api.getRow(cp2.id).fechaRaw),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <IconButton color="primary" onClick={() => navigate(`/ver-tesis/${params.row.id}/${params.row.pacienteId}`)}>
          <VisibilityIcon />
        </IconButton>
      )
    }
  ];

  return (
    <>
      <Typography variant="h6" gutterBottom color="#0D6AEE">
        Cuestionarios Tesis Torres
      </Typography>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Filtrar por Paciente"
            value={filterPaciente}
            onChange={e => setFilterPaciente(e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Filtrar por Profesional"
            value={filterProfesional}
            onChange={e => setFilterProfesional(e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item>
          <ToggleButtonGroup
            value={currentSort}
            exclusive
            size="small"
            onChange={(_, val) => {
              const opt = SORT_OPTIONS.find(o => o.value === val);
              if (opt) setSortModel(opt.model);
            }}
          >
            {SORT_OPTIONS.map(o => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Grid>
        <Grid item>
          <Button variant="contained" color="secondary" onClick={fetchAll} startIcon={<RestartAltIcon />}>
            Refrescar
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <LinearProgress />
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5,10,25,50]}
            slots={{ toolbar: GridToolbar }}
            disableSelectionOnClick
            sortModel={sortModel}
            onSortModelChange={setSortModel}
          />
        </Box>
      )}
    </>
  );
}
